import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Empty,
  Progress,
  Skeleton,
  Space,
  Tag,
  Typography,
} from "antd";
import {
  ArrowLeftOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  ReloadOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { sanitizeHtml } from "../../utils/sanitizeHtml";
import { fetchData, postData } from "../../api/apiService";
import { AI } from "../../api/endpointMap";

const { Title, Paragraph, Text } = Typography;

// Module 11 §4.2 line 72 — `/embed/generate-response/{id}` AI response
// generation flow inside the iframe. v1 is income-tax-only (the only
// portal where with_legal=true cleanly works in production today).
// GST + TDS render a "not supported in this surface" empty-state so
// partners hitting the URL get a clear answer instead of a silent 500.
//
// Flow:
//   1. Mount → fetch the notice detail (e-proceeding first, demand
//      fallback) so we can show the user what they're about to
//      generate against. No auto-kickoff.
//   2. User clicks "Generate AI Response" → POST /ai/generate-response,
//      receive 202 + task_id, switch to polling state.
//   3. Poll GET /ai/generate-response/status/{task_id} every 3s until
//      `done` or `failed`. Show progress bar with backend-supplied
//      stage + percent.
//   4. Done → render the response HTML through DOMPurify. The BE-
//      returned content is server-trusted but we sanitize anyway since
//      this is rendered inside a partner iframe — defence-in-depth.
//   5. Failed → error alert with retry button (re-enables the kickoff).
//
// Out of scope (matches §3.3 / iframe-as-read-mostly):
//   - "Pick legal issues" form — too heavy for an iframe; we let BE
//     auto-identify via `with_legal: true`.
//   - Submit-insufficient-data follow-up — multi-step; partner can
//     show a re-auth + retry message in their own UI instead.
//   - PDF generation — partner app handles export.
//   - Finalize / save-draft — read-mostly surface.

const POLL_INTERVAL_MS = 3000;
// Safety cap on poll attempts — backend's heaviest path takes ~90s
// in production; 60 attempts × 3s = 180s gives us 2× headroom before
// we surface a "took too long" message.
const POLL_MAX_ATTEMPTS = 60;

// Map portal URL segment → notice-detail endpoint factory. Mirrors the
// per-portal config in EmbedNoticeDetail (kept inline here to avoid a
// shared-file dance for two consumers).
const PORTAL_DETAIL = {
  "income-tax": (id) => `/api/v1/income-tax/e-proceedings/${id}`,
  // Fallback for IT — some rows are demands not e-proceedings.
  "income-tax-fallback": (id) => `/api/v1/income-tax/demands/${id}`,
  // GST + TDS exposed only so the page can render a "not supported"
  // empty-state for them rather than 500'ing.
  gst: (id) => `/api/v1/gst/notices/${id}`,
  tds: (id) => `/api/v1/tds/notices/${id}`,
};

// BE wraps tenant API responses as `{success, data: ...}` (the
// GenericResponse envelope). This is different from the admin
// `{message, result}` envelope the embed pages mostly consume — peel
// `data` off when present, fall through otherwise.
const unwrapAi = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  if (raw.data && typeof raw.data === "object") return raw.data;
  if (raw.result && typeof raw.result === "object") return raw.result;
  return raw;
};

// Detail fetch tolerant unwrap — handles both `{data}` and `{result}`
// envelopes since GST + TDS detail endpoints use one and IT uses the
// other. Same pattern as EmbedNoticeDetail.
const unwrapDetail = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  if (raw.data && typeof raw.data === "object") {
    if (Array.isArray(raw.data.records)) return raw.data.records[0] || null;
    return raw.data;
  }
  if (raw.result && typeof raw.result === "object") return raw.result;
  return raw;
};

// Pull the response HTML out of the BE's polymorphic `result` field.
// Observed shapes (locked via local-BE smoke):
//   1. result is a string (Gen-2 sync path) — return as-is.
//   2. result.response is a string (Gen-3 async-task SUCCESS path —
//      the dominant case for the iframe in production).
//   3. result.message is a string (DB-fallback path when Redis evicted
//      the Celery state but the response was persisted).
//   4. result.response_html / result.response_text (Gen-3 alt keys
//      for forward-compat — kept defensively).
const pickResponseHtml = (result) => {
  if (!result) return null;
  if (typeof result === "string") return result;
  if (typeof result.response === "string") return result.response;
  if (typeof result.message === "string") return result.message;
  if (typeof result.response_html === "string") return result.response_html;
  if (typeof result.response_text === "string") return result.response_text;
  return null;
};

const EmbedGenerateResponse = () => {
  const navigate = useNavigate();
  const { noticeId } = useParams();
  const [searchParams] = useSearchParams();
  // Default to income-tax — the only portal we support for v1. Partners
  // explicitly passing ?portal=gst|tds get the unsupported-portal view.
  const portal = searchParams.get("portal") || "income-tax";

  const [notice, setNotice] = useState(null);
  const [noticeLoading, setNoticeLoading] = useState(true);
  const [noticeError, setNoticeError] = useState(null);

  // Generation state machine. `idle` → user can click Generate. `queued`
  // / `processing` → poller is running. `done` → response is rendered.
  // `failed` → error alert + retry button.
  const [generation, setGeneration] = useState({
    state: "idle",
    taskId: null,
    stage: null,
    percent: 0,
    responseHtml: null,
    error: null,
  });

  // Poll-loop bookkeeping kept in refs so timer cleanup doesn't depend
  // on stale state captures.
  const pollTimerRef = useRef(null);
  const pollAttemptsRef = useRef(0);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  // Cleanup any in-flight poll on unmount. Defensive — iframe unmount
  // usually means the React tree is gone, but covers HMR + route swaps.
  useEffect(() => () => stopPolling(), [stopPolling]);

  // Fetch the notice detail so the user sees what they're generating
  // against. Same dual-endpoint pattern as EmbedNoticeDetail (income-
  // tax tries e-proceedings then falls back to demands).
  useEffect(() => {
    if (!noticeId) return;
    let cancelled = false;
    setNoticeLoading(true);
    setNoticeError(null);

    const tryFetch = async (factoryKey) => {
      const factory = PORTAL_DETAIL[factoryKey];
      if (!factory) return null;
      const raw = await fetchData(factory(noticeId));
      return unwrapDetail(raw);
    };

    (async () => {
      try {
        if (portal === "income-tax") {
          let row = null;
          try {
            row = await tryFetch("income-tax");
          } catch {
            row = null;
          }
          if (!row) {
            try {
              row = await tryFetch("income-tax-fallback");
            } catch {
              row = null;
            }
          }
          if (cancelled) return;
          if (!row) {
            setNoticeError("Notice not found");
          } else {
            setNotice(row);
          }
        } else if (portal === "gst" || portal === "tds") {
          // We don't load the notice for GST/TDS — the unsupported-
          // portal view is rendered without it.
          setNotice(null);
        } else {
          setNoticeError("Unsupported portal");
        }
      } catch (e) {
        if (!cancelled) {
          setNoticeError(
            e?.detail || e?.message || "Failed to load notice"
          );
        }
      } finally {
        if (!cancelled) setNoticeLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [noticeId, portal]);

  const pollOnce = useCallback(
    async (taskId) => {
      try {
        const raw = await fetchData(AI.GENERATE_RESPONSE_STATUS(taskId));
        const data = unwrapAi(raw) || {};
        if (data.status === "processing") {
          setGeneration((g) => ({
            ...g,
            state: "processing",
            stage: data.stage || "Working",
            percent: Number(data.percent) || 0,
          }));
          return false; // not terminal — keep polling
        }
        if (data.status === "done") {
          const html = pickResponseHtml(data.result);
          setGeneration((g) => ({
            ...g,
            state: "done",
            stage: "Done",
            percent: 100,
            responseHtml: html,
            error: html
              ? null
              : "Generation reported done but no response body was returned.",
          }));
          return true;
        }
        if (data.status === "failed") {
          setGeneration((g) => ({
            ...g,
            state: "failed",
            error:
              typeof data.error === "string"
                ? data.error
                : "Generation failed",
          }));
          return true;
        }
        // Unknown status — treat as still-pending; the BE PENDING branch
        // returns no `status` field on the wire if Redis evicted state.
        setGeneration((g) => ({
          ...g,
          state: "queued",
          stage: g.stage || "Queued",
        }));
        return false;
      } catch (e) {
        // 401 → axios interceptor postMessages the parent. 404 means
        // the task is unknown to BE (cross-tenant or evicted) — surface
        // as failed so the user can retry instead of polling forever.
        const status = e?.response?.status;
        if (status === 404) {
          setGeneration((g) => ({
            ...g,
            state: "failed",
            error: "Task not found. Please retry.",
          }));
          return true;
        }
        // Transient error — count toward the cap but keep polling.
        return false;
      }
    },
    [] // no closure deps — pollOnce uses functional setGeneration only
  );

  const schedulePoll = useCallback(
    (taskId) => {
      pollTimerRef.current = setTimeout(async () => {
        pollAttemptsRef.current += 1;
        const terminal = await pollOnce(taskId);
        if (terminal) {
          stopPolling();
          return;
        }
        if (pollAttemptsRef.current >= POLL_MAX_ATTEMPTS) {
          setGeneration((g) => ({
            ...g,
            state: "failed",
            error:
              "Response generation took too long. Please retry, or open in your tenant app to monitor.",
          }));
          stopPolling();
          return;
        }
        schedulePoll(taskId);
      }, POLL_INTERVAL_MS);
    },
    [pollOnce, stopPolling]
  );

  const startGeneration = useCallback(async () => {
    if (!notice) return;
    // Reset state for retry case — clears any previous error/responseHtml.
    pollAttemptsRef.current = 0;
    stopPolling();
    setGeneration({
      state: "queued",
      taskId: null,
      stage: "Identifying issues…",
      percent: 0,
      responseHtml: null,
      error: null,
    });

    const doctype = notice.notice_type || "E Proceeding";
    const docName = notice.name || notice.id || noticeId;

    try {
      // Step 1 — kick off with `with_legal: true`. BE responds in one
      // of three shapes:
      //   (a) `{status: "needs_selection", response: {issues: [...]}}` —
      //       BE auto-identified legal issues; FE re-POSTs in step 2
      //       with the same issues handed back via `custom_legal_issues`.
      //   (b) `{task_id: "..."}` (with status_code 202) — BE went
      //       straight to async. Skip step 2, jump to polling.
      //   (c) Synchronous result (legacy sync path) — return the body
      //       as the final result and bail.
      const step1Raw = await postData(AI.GENERATE_RESPONSE, {
        doctype_name: doctype,
        doc_name: docName,
        with_legal: true,
        proceed_with_available: true,
      });
      const step1 = unwrapAi(step1Raw) || {};

      // Branch (b) — already async.
      if (step1.task_id || step1.taskId) {
        const taskId = step1.task_id || step1.taskId;
        setGeneration((g) => ({
          ...g,
          state: "queued",
          taskId,
          stage: "Queued",
        }));
        schedulePoll(taskId);
        return;
      }

      // Branch (c) — sync HTML returned inline.
      if (step1.status !== "needs_selection") {
        const html = pickResponseHtml(step1.result || step1.response || step1);
        setGeneration((g) => ({
          ...g,
          state: html ? "done" : "failed",
          stage: html ? "Done" : null,
          percent: html ? 100 : 0,
          responseHtml: html,
          error: html
            ? null
            : "Server returned an unexpected response shape.",
        }));
        return;
      }

      // Branch (a) — needs_selection. Auto-confirm all issues BE
      // identified and POST step 2. v1 scope: no UI for picking
      // individual issues per spec (iframe is read-mostly + the
      // issue-picker form is too heavy for an embed surface). If
      // a partner asks for selective generation later, lift
      // EPForm.jsx's selector into a separate iframe-aware page.
      const issues = step1?.response?.issues;
      if (!Array.isArray(issues) || issues.length === 0) {
        setGeneration((g) => ({
          ...g,
          state: "failed",
          error:
            "No legal issues identified for this notice. Open in your tenant app to compose a response manually.",
        }));
        return;
      }

      setGeneration((g) => ({
        ...g,
        stage: "Submitting…",
        percent: 10,
      }));

      // Step 2 — pass the BE-returned issues straight back as
      // `custom_legal_issues.issues`. With ASYNC_GENERATION_ENABLED on
      // (production default) this returns 202 + task_id; with it off,
      // the response is the final synchronous body.
      const step2Raw = await postData(AI.GENERATE_RESPONSE, {
        doctype_name: doctype,
        doc_name: docName,
        with_legal: true,
        custom_legal_issues: { issues },
        proceed_with_available: true,
      });
      const step2 = unwrapAi(step2Raw) || {};

      const taskId = step2.task_id || step2.taskId;
      if (taskId) {
        setGeneration((g) => ({
          ...g,
          state: "queued",
          taskId,
          stage: "Queued",
        }));
        schedulePoll(taskId);
        return;
      }

      // Sync fallback on step 2.
      const html = pickResponseHtml(step2.result || step2.response || step2);
      setGeneration((g) => ({
        ...g,
        state: html ? "done" : "failed",
        stage: html ? "Done" : null,
        percent: html ? 100 : 0,
        responseHtml: html,
        error: html
          ? null
          : "Server returned an unexpected response shape after issue selection.",
      }));
    } catch (e) {
      // 403 with quota error — surface the BE's humanised message.
      // Plan-Quotas-B uses { detail: <string with N/M counts> }.
      const status = e?.response?.status;
      let msg =
        typeof e?.detail === "string"
          ? e.detail
          : e?.message || "Failed to start generation";
      if (status === 403 && /limit|quota|upgrade/i.test(msg)) {
        msg = `Quota exceeded: ${msg}`;
      }
      setGeneration({
        state: "failed",
        taskId: null,
        stage: null,
        percent: 0,
        responseHtml: null,
        error: msg,
      });
    }
  }, [notice, noticeId, schedulePoll, stopPolling]);

  // ---------- Render branches ----------

  // GST / TDS — clean unsupported-portal message.
  if (portal !== "income-tax") {
    return (
      <div className="p-4 sm:p-6 max-w-3xl mx-auto">
        <Breadcrumb
          className="mb-3"
          items={[
            { title: <Link to="/embed/dashboard">Dashboard</Link> },
            { title: "Generate response" },
          ]}
        />
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span>
              AI response generation is only available for{" "}
              <Text strong>Income Tax</Text> notices in this embedded view.
              Open the notice in your full NoticeAI tenant app to generate
              responses for {portal.toUpperCase()}.
            </span>
          }
        >
          <Button
            type="primary"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(`/embed/notices/${portal}/${noticeId}`)}
          >
            Back to notice
          </Button>
        </Empty>
      </div>
    );
  }

  // Loading the notice itself.
  if (noticeLoading && !notice) {
    return (
      <div className="p-4 sm:p-6 max-w-3xl mx-auto">
        <Skeleton active paragraph={{ rows: 6 }} />
      </div>
    );
  }

  if (noticeError && !notice) {
    return (
      <div className="p-4 sm:p-6 max-w-3xl mx-auto">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Notice not found, or it doesn't belong to your organization."
        >
          <Button
            type="primary"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/embed/income-tax/notices")}
          >
            Back to notices
          </Button>
        </Empty>
      </div>
    );
  }

  const subject =
    notice?.notice_name ||
    notice?.subject ||
    notice?.notice_type ||
    "Notice";
  const status = notice?.notice_status;

  // Render the sanitized HTML for done state. DOMPurify config is
  // intentionally permissive on tags/attrs (we want tables, lists,
  // headings, anchors) but strips scripts + event handlers + javascript:
  // URLs. We're inside a partner iframe — the parent app's own CSP
  // restricts navigation, but defence-in-depth says scrub anyway.
  const sanitizedHtml = generation.responseHtml
    ? sanitizeHtml(generation.responseHtml)
    : null;

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto">
      <Breadcrumb
        className="mb-3"
        items={[
          { title: <Link to="/embed/dashboard">Dashboard</Link> },
          {
            title: (
              <Link to="/embed/income-tax/notices">Income Tax notices</Link>
            ),
          },
          {
            title: (
              <Link
                to={`/embed/notices/income-tax/${noticeId}`}
              >
                {subject}
              </Link>
            ),
          },
          { title: "Generate response" },
        ]}
      />

      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <Title level={4} className="!mb-1">
            Generate AI response
          </Title>
          <Space wrap>
            <Tag color="blue">Income Tax</Tag>
            {status && <Tag>{status}</Tag>}
            <Text type="secondary">{subject}</Text>
          </Space>
        </div>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(`/embed/notices/income-tax/${noticeId}`)}
        >
          Back to notice
        </Button>
      </div>

      {/* Idle state — consent button. */}
      {generation.state === "idle" && (
        <Card>
          <Paragraph>
            NoticeAI will analyze this notice and generate a draft legal
            response. This typically takes <Text strong>30-90 seconds</Text>
            . The response will appear in this view when ready.
          </Paragraph>
          <Paragraph type="secondary" className="text-sm">
            One generation counts against your AI response quota.
          </Paragraph>
          <Button
            type="primary"
            size="large"
            icon={<ThunderboltOutlined />}
            onClick={startGeneration}
          >
            Generate AI Response
          </Button>
        </Card>
      )}

      {/* Queued / processing — progress bar driven by BE meta. */}
      {(generation.state === "queued" ||
        generation.state === "processing") && (
        <Card>
          <Title level={5} className="!mb-3">
            {generation.stage || "Working"}
          </Title>
          <Progress
            percent={generation.percent}
            status="active"
            strokeColor="#2563eb"
          />
          <Paragraph type="secondary" className="!mt-3 !mb-0 text-sm">
            Polling every 3 seconds. You can leave this open - it'll
            update on its own.
          </Paragraph>
        </Card>
      )}

      {/* Done — render sanitized HTML inline. */}
      {generation.state === "done" && (
        <Card
          title={
            <Space>
              <CheckCircleFilled style={{ color: "#16a34a" }} />
              <span>Response ready</span>
            </Space>
          }
          extra={
            <Text type="secondary" className="text-xs">
              Read-only preview. Open in your tenant app to edit, save,
              or export.
            </Text>
          }
        >
          {sanitizedHtml ? (
            <div
              className="prose prose-sm max-w-none"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
            />
          ) : (
            <Empty description="Response generated but no body was returned. Open in your tenant app to view." />
          )}
        </Card>
      )}

      {/* Failed — error alert + retry. */}
      {generation.state === "failed" && (
        <Card>
          <Alert
            type="error"
            showIcon
            icon={<CloseCircleFilled />}
            message="Generation failed"
            description={
              generation.error || "An unexpected error occurred."
            }
            className="mb-3"
          />
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={startGeneration}
          >
            Retry
          </Button>
        </Card>
      )}
    </div>
  );
};

export default EmbedGenerateResponse;
